import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Sparkles, X, Maximize2 } from "lucide-react";
import { MenuAPI, CategoriesAPI, UploadAPI, resolveImageUrl } from "@/admin/api";
import { useAdmin } from "@/admin/store";
import { BtnGhost, BtnPrimary, Field, Toggle, inputCls, selectCls } from "@/components/admin/ui";

const SPICE_OPTIONS = [
  { level: 0, label: "None", icon: "—" },
  { level: 1, label: "Mild", icon: "🌶️" },
  { level: 2, label: "Medium", icon: "🌶️🌶️" },
  { level: 3, label: "Spicy", icon: "🌶️🌶️🌶️" },
];

const JOURNEY_OPTIONS = [
  { value: "", label: "Select a Tasting Journey" },
  { value: "coastal", label: "Coastal Journey" },
  { value: "pan-indian", label: "Pan-Indian Journey" },
];

const MenuItemForm = () => {
  const { id } = useParams();
  const { log, user } = useAdmin();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: menuItems = [] } = useQuery({ queryKey: ["menuItems"], queryFn: MenuAPI.getAll });
  const { data: categories = [] } = useQuery({ queryKey: ["categories"], queryFn: CategoriesAPI.getAll });

  const existing = id ? menuItems.find((m) => m.itemId === Number(id)) : null;

  const [form, setForm] = useState({
    itemName: "",
    categoryId: "",
    price: "",
    imagePath: "",
    description: "",
    isActive: true,
    isVegetarian: false,
    spiceLevel: 0,
    containsGluten: false,
    containsNuts: false,
    containsDairy: false,
    isFeatured: false,
    journey: "",
  });
  const [fileToUpload, setFileToUpload] = useState(null);
  const [preview, setPreview] = useState("");
  const [showEnlargedPreview, setShowEnlargedPreview] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (existing) {
      setForm({
        itemName: existing.itemName || "",
        categoryId: existing.categoryId || "",
        price: existing.price || "",
        imagePath: existing.imagePath || "",
        description: existing.description || "",
        isActive: existing.isActive ?? true,
        isVegetarian: Boolean(existing.isVegetarian),
        spiceLevel: Number(existing.spiceLevel) || 0,
        containsGluten: Boolean(existing.containsGluten),
        containsNuts: Boolean(existing.containsNuts),
        containsDairy: Boolean(existing.containsDairy),
        isFeatured: Boolean(existing.isFeatured),
        journey: existing.journey || "",
      });
      setPreview(resolveImageUrl(existing.imagePath));
    } else if (categories.length > 0 && !form.categoryId) {
      setForm((s) => ({ ...s, categoryId: categories[0].categoryId }));
    }
  }, [existing, categories, form.categoryId]);

  const catName = (cid) => categories.find((c) => c.categoryId === Number(cid))?.categoryName;

  const selectedCategory = categories.find((c) => c.categoryId === Number(form.categoryId));
  const isTastingCategory = Boolean(
    selectedCategory && (
      selectedCategory.categoryName?.toLowerCase().includes("tasting") ||
      selectedCategory.slug?.toLowerCase().includes("tasting")
    )
  );

  const createMutation = useMutation({
    mutationFn: async (payload) => {
      let imagePath = payload.imagePath;
      if (fileToUpload) {
        imagePath = await UploadAPI.uploadFile(fileToUpload, "menu");
      }
      const itemPayload = { ...payload, imagePath, createdBy: user?.userId || 1 };
      return await MenuAPI.create(itemPayload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["menuItems"]);
      log("Menu item added", form.itemName);
      navigate("/admin/menu");
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, payload }) => {
      let imagePath = payload.imagePath;
      if (fileToUpload) {
        imagePath = await UploadAPI.uploadFile(fileToUpload, "menu");
      }
      const itemPayload = { ...payload, imagePath };
      await MenuAPI.update(id, itemPayload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["menuItems"]);
      log("Dish updated", form.itemName);
      navigate("/admin/menu");
    },
  });

  const handleImageChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setFileToUpload(file);
      setPreview(URL.createObjectURL(file));
    }
  };

  const save = () => {
    const errs = {};
    if (!form.itemName.trim()) errs.name = "The dish needs a name.";
    if (!form.price || Number(form.price) <= 0) errs.price = "Set a price.";
    setErrors(errs);
    if (Object.keys(errs).length) return;

    const payload = {
      ...form,
      categoryId: Number(form.categoryId),
      price: Number(form.price),
      spiceLevel: Number(form.spiceLevel),
      isVegetarian: Boolean(form.isVegetarian),
      containsGluten: Boolean(form.containsGluten),
      containsNuts: Boolean(form.containsNuts),
      containsDairy: Boolean(form.containsDairy),
      isFeatured: Boolean(form.isFeatured),
      journey: isTastingCategory && form.journey ? form.journey : null,
    };

    if (existing) {
      updateMutation.mutate({ id: existing.itemId, payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  const isSaving = createMutation.isPending || updateMutation.isPending;

  return (
    <div data-testid="menu-item-form">
      <button
        data-testid="menu-form-back"
        onClick={() => navigate("/admin/menu")}
        className="mb-8 flex items-center gap-3 font-body text-[10px] tracking-[0.35em] uppercase text-cream/55 transition-colors hover:text-brass"
      >
        <ArrowLeft size={14} strokeWidth={1.5} /> Back to the Oonu
      </button>

      <h1 className="font-display text-3xl tracking-tight text-ivory">
        {existing ? "EDIT DISH" : "ADD DISH"}
      </h1>
      <p className="mt-2 font-body text-sm font-light text-cream/55">
        {existing ? `Adjusting “${existing.itemName}”.` : "A new plate for the house."}
      </p>

      <div className="mt-12 grid gap-12 lg:grid-cols-2">
        <div className="space-y-6">
          <Field label="Item Name" error={errors.name}>
            <input
              data-testid="dish-form-name"
              className={inputCls}
              value={form.itemName}
              onChange={(e) => setForm({ ...form, itemName: e.target.value })}
              placeholder="Karimeen Pollichathu"
            />
          </Field>

          <div className="grid grid-cols-2 gap-4">
            <Field label="Category">
              <select
                data-testid="dish-form-category"
                className={selectCls}
                value={form.categoryId}
                onChange={(e) => {
                  const newCatId = e.target.value;
                  const newCat = categories.find((c) => c.categoryId === Number(newCatId));
                  const isTasting = Boolean(
                    newCat && (
                      newCat.categoryName?.toLowerCase().includes("tasting") ||
                      newCat.slug?.toLowerCase().includes("tasting")
                    )
                  );
                  setForm({
                    ...form,
                    categoryId: newCatId,
                    journey: isTasting ? form.journey : "",
                  });
                }}
              >
                {categories.filter((c) => !c.isDeleted).map((c) => (
                  <option key={c.categoryId} value={c.categoryId}>{c.categoryName}</option>
                ))}
              </select>
            </Field>
            <Field label="Price (₹)" error={errors.price}>
              <input
                data-testid="dish-form-price"
                type="number"
                min="0"
                className={inputCls}
                value={form.price}
                onChange={(e) => setForm({ ...form, price: e.target.value })}
                placeholder="680"
              />
            </Field>
          </div>

          <Field label="Food Image">
            <label className="flex h-40 cursor-pointer items-center justify-center border border-dashed border-cream/20 bg-bark/50 transition-colors hover:border-brass/50">
              {preview ? (
                <img src={preview} alt="Preview" className="h-full w-full object-cover" />
              ) : (
                <span className="font-body text-[10px] tracking-[0.3em] uppercase text-cream/40">
                  Choose a photograph
                </span>
              )}
              <input
                data-testid="dish-form-image"
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleImageChange}
              />
            </label>
          </Field>

          <Field label="Description">
            <textarea
              data-testid="dish-form-description"
              rows={4}
              className={inputCls}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Pearl spot wrapped in banana leaf..."
            />
          </Field>

          {/* Dietary & Spice Section */}
          <div className="border border-cream/10 bg-bark/40 p-5 space-y-5">
            <h3 className="font-body text-[11px] tracking-[0.3em] uppercase text-brass font-medium">
              Dietary & Allergens
            </h3>

            {/* Vegetarian Toggle */}
            <div className="flex items-center justify-between border-b border-cream/10 pb-4">
              <div>
                <span className="block font-body text-xs text-cream/90">Vegetarian (Veg)</span>
                <span className="font-body text-[10px] text-cream/40">Is this dish vegetarian or plant-based?</span>
              </div>
              <div className="flex items-center gap-3">
                <span className={`font-body text-[10px] tracking-wider uppercase ${form.isVegetarian ? "text-emerald-400 font-semibold" : "text-cream/40"}`}>
                  {form.isVegetarian ? "VEG" : "NON-VEG"}
                </span>
                <Toggle
                  testId="dish-form-veg"
                  checked={form.isVegetarian}
                  onChange={(v) => setForm({ ...form, isVegetarian: v })}
                />
              </div>
            </div>

            {/* Spice Level Selector */}
            <div className="border-b border-cream/10 pb-4">
              <span className="mb-2 block font-body text-[10px] tracking-[0.3em] uppercase text-cream/50">
                Spice Level
              </span>
              <div className="grid grid-cols-4 gap-2">
                {SPICE_OPTIONS.map((opt) => {
                  const active = form.spiceLevel === opt.level;
                  return (
                    <button
                      key={opt.level}
                      type="button"
                      data-testid={`dish-spice-${opt.level}`}
                      onClick={() => setForm({ ...form, spiceLevel: opt.level })}
                      className={`flex flex-col items-center justify-center border py-2 px-1 transition-all ${
                        active
                          ? "border-brass bg-brass/15 text-brass"
                          : "border-cream/15 bg-bark text-cream/50 hover:border-cream/40"
                      }`}
                    >
                      <span className="text-sm">{opt.icon}</span>
                      <span className="mt-1 font-body text-[9px] tracking-wider uppercase">
                        {opt.label}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Allergen Indicators: Gluten, Nuts, Dairy */}
            <div>
              <span className="mb-2 block font-body text-[10px] tracking-[0.3em] uppercase text-cream/50">
                Allergen Contains
              </span>
              <div className="grid grid-cols-3 gap-2">
                {/* Contains Gluten */}
                <button
                  type="button"
                  data-testid="dish-allergen-gluten"
                  onClick={() => setForm({ ...form, containsGluten: !form.containsGluten })}
                  className={`flex items-center justify-center gap-2 border py-2.5 px-2 transition-all ${
                    form.containsGluten
                      ? "border-amber-400/80 bg-amber-500/15 text-amber-200"
                      : "border-cream/15 bg-bark text-cream/40 hover:border-cream/30"
                  }`}
                >
                  <span className="font-mono text-xs font-bold border border-current px-1.5 py-0.5 rounded-sm">G</span>
                  <span className="font-body text-[10px] tracking-wider uppercase">Gluten</span>
                </button>

                {/* Contains Nuts */}
                <button
                  type="button"
                  data-testid="dish-allergen-nuts"
                  onClick={() => setForm({ ...form, containsNuts: !form.containsNuts })}
                  className={`flex items-center justify-center gap-2 border py-2.5 px-2 transition-all ${
                    form.containsNuts
                      ? "border-orange-400/80 bg-orange-500/15 text-orange-200"
                      : "border-cream/15 bg-bark text-cream/40 hover:border-cream/30"
                  }`}
                >
                  <span className="font-mono text-xs font-bold border border-current px-1.5 py-0.5 rounded-sm">N</span>
                  <span className="font-body text-[10px] tracking-wider uppercase">Nuts</span>
                </button>

                {/* Contains Dairy */}
                <button
                  type="button"
                  data-testid="dish-allergen-dairy"
                  onClick={() => setForm({ ...form, containsDairy: !form.containsDairy })}
                  className={`flex items-center justify-center gap-2 border py-2.5 px-2 transition-all ${
                    form.containsDairy
                      ? "border-sky-400/80 bg-sky-500/15 text-sky-200"
                      : "border-cream/15 bg-bark text-cream/40 hover:border-cream/30"
                  }`}
                >
                  <span className="font-mono text-xs font-bold border border-current px-1.5 py-0.5 rounded-sm">D</span>
                  <span className="font-body text-[10px] tracking-wider uppercase">Dairy</span>
                </button>
              </div>
            </div>
          </div>

          {/* Highlights & Tasting Journey */}
          <div className="border border-cream/10 bg-bark/40 p-5 space-y-4">
            <h3 className="font-body text-[11px] tracking-[0.3em] uppercase text-brass font-medium">
              Menu Classification
            </h3>

            {/* Chef's Signature Pick */}
            <div className={`flex items-center justify-between ${isTastingCategory ? "border-b border-cream/10 pb-4" : ""}`}>
              <div>
                <span className="flex items-center gap-1.5 font-body text-xs text-cream/90">
                  <Sparkles size={13} className="text-brass" /> Chef's Signature Pick
                </span>
                <span className="font-body text-[10px] text-cream/40">Feature this dish prominently on the menu</span>
              </div>
              <Toggle
                testId="dish-form-featured"
                checked={form.isFeatured}
                onChange={(v) => setForm({ ...form, isFeatured: v })}
              />
            </div>

            {/* Tasting Journey (Only for Tasting Menu category) */}
            {isTastingCategory && (
              <Field label="Tasting Journey Association">
                <select
                  data-testid="dish-form-journey"
                  className={selectCls}
                  value={form.journey || ""}
                  onChange={(e) => setForm({ ...form, journey: e.target.value })}
                >
                  {JOURNEY_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </Field>
            )}
          </div>

          {/* Active Status Toggle */}
          <div className="flex items-center gap-4">
            <Toggle testId="dish-form-active" checked={form.isActive} onChange={(v) => setForm({ ...form, isActive: v })} />
            <span className="font-body text-[10px] tracking-[0.3em] uppercase text-cream/55">
              {form.isActive ? "Shown on the Oonu" : "Hidden from the Oonu"}
            </span>
          </div>

          {/* Form Actions */}
          <div className="flex gap-3 pt-4">
            <BtnGhost onClick={() => navigate("/admin/menu")} disabled={isSaving}>Cancel</BtnGhost>
            <BtnPrimary data-testid="dish-form-save" onClick={save} disabled={isSaving}>
              {isSaving ? "Saving..." : (existing ? "Save Changes" : "Add to the Oonu")}
            </BtnPrimary>
          </div>
        </div>

        {/* Live Preview Card */}
        <div className="lg:sticky lg:top-12 lg:self-start">
          <p className="mb-5 font-body text-[9px] tracking-[0.4em] uppercase text-cream/40">
            As it will appear on the Oonu
          </p>
          <div className="border border-cream/12 bg-bark/40 p-6 md:p-8" data-testid="dish-preview">
            <div
              className={`relative mx-auto w-3/4 overflow-hidden rounded-t-full border border-brass/20 ${preview ? "cursor-pointer group hover:border-brass transition-colors" : ""}`}
              onClick={() => preview && setShowEnlargedPreview(true)}
              title={preview ? "Click to enlarge photograph" : ""}
            >
              {preview ? (
                <>
                  <img src={preview} alt={form.itemName} className="aspect-[3/4] w-full object-cover transition-transform duration-300 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-black/35 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <Maximize2 size={22} className="text-cream" />
                  </div>
                </>
              ) : (
                <div className="flex aspect-[3/4] w-full items-center justify-center bg-bark">
                  <span className="font-body text-[9px] tracking-[0.3em] uppercase text-cream/30">No photograph yet</span>
                </div>
              )}
              {form.isFeatured && (
                <div className="absolute top-3 right-3 rounded-full bg-brass/90 text-bark px-2.5 py-1 text-[9px] font-bold tracking-widest uppercase shadow">
                  ★ Chef's Pick
                </div>
              )}
            </div>

            <div className="mt-8 text-center">
              <div className="flex items-center justify-center gap-2">
                <p className="font-body text-[10px] tracking-[0.4em] uppercase text-brass">{catName(form.categoryId) || "—"}</p>
                {form.isVegetarian && (
                  <span className="border border-emerald-500/60 bg-emerald-500/10 text-emerald-400 text-[8px] font-bold px-1.5 py-0.5 rounded-sm">
                    VEG
                  </span>
                )}
              </div>

              <h3 className="mt-3 font-display text-2xl tracking-tight text-ivory">
                {form.itemName ? form.itemName.toUpperCase() : "THE DISH NAME"}
              </h3>
              <p className="mx-auto mt-4 max-w-xs font-body text-sm font-light leading-relaxed text-cream/65">
                {form.description || "A short, poetic description of the dish will rest here."}
              </p>
              <p className="mt-5 font-display text-xl italic text-brass">₹ {form.price || "—"}</p>

              {/* Dynamic Dietary / Allergen Badges in Preview */}
              <div className="mt-5 flex flex-wrap justify-center items-center gap-1.5">
                {form.spiceLevel > 0 && (
                  <span className="inline-flex items-center gap-1 border border-cream/15 px-2.5 py-0.5 font-body text-[9px] tracking-[0.15em] uppercase text-cream/70">
                    {"🌶️".repeat(form.spiceLevel)} {form.spiceLevel === 1 ? "Mild" : form.spiceLevel === 2 ? "Medium" : "Spicy"}
                  </span>
                )}
                {form.containsGluten && (
                  <span className="inline-flex items-center gap-1 border border-amber-500/40 bg-amber-500/10 px-2 py-0.5 font-body text-[9px] tracking-wider text-amber-300">
                    <span className="font-bold">G</span> Gluten
                  </span>
                )}
                {form.containsNuts && (
                  <span className="inline-flex items-center gap-1 border border-orange-500/40 bg-orange-500/10 px-2 py-0.5 font-body text-[9px] tracking-wider text-orange-300">
                    <span className="font-bold">N</span> Nuts
                  </span>
                )}
                {form.containsDairy && (
                  <span className="inline-flex items-center gap-1 border border-sky-500/40 bg-sky-500/10 px-2 py-0.5 font-body text-[9px] tracking-wider text-sky-300">
                    <span className="font-bold">D</span> Dairy
                  </span>
                )}
                {isTastingCategory && form.journey && (
                  <span className="inline-flex items-center gap-1 border border-brass/40 bg-brass/10 px-2.5 py-0.5 font-body text-[9px] tracking-widest uppercase text-brass">
                    {form.journey === "coastal" ? "Coastal Journey" : "Pan-Indian Journey"}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Enlarged Photo Lightbox Modal */}
      {showEnlargedPreview && preview && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 backdrop-blur-md p-4"
          onClick={() => setShowEnlargedPreview(false)}
        >
          <div
            className="relative max-w-xl w-full bg-bark border border-cream/20 rounded-2xl p-6 text-center shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowEnlargedPreview(false)}
              aria-label="Close"
              className="absolute top-4 right-4 z-10 h-8 w-8 rounded-full bg-ink/70 hover:bg-terra text-cream flex items-center justify-center transition-colors"
            >
              <X size={16} />
            </button>
            <div className="overflow-hidden rounded-xl border border-cream/15 max-h-[70vh] bg-ink flex items-center justify-center">
              <img src={preview} alt={form.itemName} className="max-h-[70vh] w-auto object-contain" />
            </div>
            <h3 className="mt-4 font-display text-2xl text-ivory tracking-tight">{form.itemName || "Dish Preview"}</h3>
            <p className="font-body text-xs text-brass tracking-widest uppercase mt-1">
              {catName(form.categoryId) || "Veedu"} {form.price ? `• ₹${form.price}` : ""}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default MenuItemForm;
